import apiClient from '@/lib/api/apiClient'
import { API_ENDPOINTS } from '@/lib/api/config'
import { getBrowserApiBaseUrl } from '@/config/env'
import type {
  PaginatedResidentResponse,
  ResidentApplicationData,
  ResidentDetailResponse,
  ResidentListQuery,
  ResidentStatusListResponse,
  RentalListResponse,
  SelectOption,
  ClaimDenyReopenPayload,
  LookupRecord,
} from '../types'
import { buildRentalListFromStatusList } from '../utils/residentListConstants'
import {
  buildResidentListRequestParams,
  serializeResidentExportParams,
} from '../utils/residentQuery'
import {
  normalizeResidentDetail,
  type ResidentDetailPageType,
} from '../utils/residentDecoupling'
import { normalizeResidentDetailDocuments } from '../utils/residentDocuments'

/** Detect Angular-style resident GET payloads vs `{ data: {...} }` wrappers from some API builds. */
function looksLikeResidentDetailPayload(o: Record<string, unknown>): boolean {
  return (
    'resident_default' in o ||
    'reimbursement' in o ||
    'reimbursement_detail' in o ||
    'resident' in o ||
    'default_process_data' in o ||
    'defaultProcessData' in o ||
    'payment_review_log' in o ||
    'payment_history' in o ||
    'document' in o ||
    'documentHistory' in o
  )
}

/**
 * GET `/api/default/:userId` adapter: unwrap wrappers, map `reimbursement` → `resident_default`,
 * normalize `document` / `documents`.
 */
export function normalizeDefaultDetailGetResponse(
  raw: unknown,
  pageType: ResidentDetailPageType = 'resident-detail',
): ResidentDetailResponse {
  const unwrapped = unwrapResidentDetailResponse(raw)
  const decoupled =
    normalizeResidentDetail(unwrapped as Record<string, unknown>, pageType) ?? unwrapped
  return normalizeResidentDetailDocuments(decoupled as ResidentDetailResponse)
}

export function unwrapResidentDetailResponse(raw: unknown, depth = 0): ResidentDetailResponse {
  if (raw === null || typeof raw !== 'object' || depth > 10) return raw as ResidentDetailResponse
  const o = raw as Record<string, unknown>
  if (looksLikeResidentDetailPayload(o)) return o as ResidentDetailResponse
  for (const key of ['data', 'result', 'payload', 'response', 'body'] as const) {
    const inner = o[key]
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      const nested = unwrapResidentDetailResponse(inner, depth + 1)
      if (looksLikeResidentDetailPayload(nested as Record<string, unknown>)) {
        return nested as ResidentDetailResponse
      }
    }
  }
  return o as ResidentDetailResponse
}

function looksLikeApplicationPayload(o: Record<string, unknown>): boolean {
  return (
    'application_id' in o ||
    'applicant_id' in o ||
    'contract_start_date' in o ||
    'rent_amount' in o ||
    'co_resident_application' in o
  )
}

/**
 * Some environments wrap `/api/application/:id` as `{ data: { ... } }`; Laravel may use `id` instead of `application_id`.
 */
export function unwrapApplicationResponse(raw: unknown, depth = 0): ResidentApplicationData {
  if (raw === null || typeof raw !== 'object' || depth > 10) return raw as ResidentApplicationData
  const o = raw as Record<string, unknown>
  if (looksLikeApplicationPayload(o)) {
    const out = { ...o }
    if (out.application_id == null && out.id != null) out.application_id = out.id as string | number
    return out as ResidentApplicationData
  }
  for (const key of ['data', 'result', 'payload', 'response', 'body'] as const) {
    const inner = o[key]
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      const nested = unwrapApplicationResponse(inner, depth + 1)
      if (looksLikeApplicationPayload(nested as Record<string, unknown>)) return nested
    }
  }
  const out = { ...o }
  if (out.application_id == null && out.id != null) out.application_id = out.id as string | number
  return out as ResidentApplicationData
}

function normalizeNumericPathSegment(id: string | number): string {
  if (typeof id === 'number') return String(id)
  const t = id.trim()
  if (/^\d+$/.test(t)) return String(Number(t))
  return id
}

function optionFromCompany(company: LookupRecord): SelectOption {
  return {
    label: String(company.company_name ?? company.label ?? ''),
    value: String(company.company_id ?? company.value ?? ''),
  }
}

function optionFromProperty(property: LookupRecord): SelectOption {
  return {
    label: String(property.property_name ?? property.label ?? ''),
    value: String(property.property_id ?? property.value ?? ''),
  }
}

function optionFromUser(user: LookupRecord): SelectOption {
  return {
    label: String(user.name ?? user.full_name ?? user.label ?? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()),
    value: String(user.user_id ?? user.id ?? user.value ?? ''),
  }
}

export async function fetchResidentList(query: Partial<ResidentListQuery>): Promise<PaginatedResidentResponse> {
  const { data } = await apiClient.get<PaginatedResidentResponse>('/api/resident', {
    params: buildResidentListRequestParams(query),
  })
  return {
    ...data,
    data: data.data || [],
  }
}

let statusListCache: Promise<ResidentStatusListResponse> | null = null

export async function fetchStatusList(): Promise<ResidentStatusListResponse> {
  if (!statusListCache) {
    statusListCache = apiClient
      .get<ResidentStatusListResponse>(API_ENDPOINTS.STATUS_LIST)
      .then((response) => response.data)
      .catch((error) => {
        statusListCache = null
        throw error
      })
  }
  return statusListCache
}

export async function fetchCompanies(search?: string): Promise<SelectOption[]> {
  const { data } = await apiClient.get<LookupRecord[]>(API_ENDPOINTS.ALL_COMPANIES, {
    params: search ? { q: search } : undefined,
  })
  return (data || []).map(optionFromCompany).filter((option) => option.value && option.label)
}

export async function fetchProperties(search?: string, companyId?: string): Promise<SelectOption[]> {
  const { data } = await apiClient.get<LookupRecord[]>(API_ENDPOINTS.ALL_PROPERTY, {
    params: { ...(search ? { q: search } : {}), ...(companyId ? { company_id: companyId } : {}) },
  })
  return (data || []).map(optionFromProperty).filter((option) => option.value && option.label)
}

export async function fetchSalesUsers(): Promise<SelectOption[]> {
  const { data } = await apiClient.get<LookupRecord[]>('/api/all_sales_user')
  return (data || []).map(optionFromUser).filter((option) => option.value && option.label)
}

export function buildResidentExportUrl(query: Partial<ResidentListQuery>, token?: string | null): string {
  return `${getBrowserApiBaseUrl()}/api/export/resident?${serializeResidentExportParams(query, token)}`
}

export async function updateResidentPaid(applicantIds: Array<string | number>, defaultMonthPaid: string): Promise<unknown> {
  const { data } = await apiClient.post('/api/default_paid', {
    applicant_ids: applicantIds,
    default_month_paid: defaultMonthPaid,
  })
  return data
}

export async function updateResidentsStatus(payload: Record<string, unknown>): Promise<unknown> {
  const { data } = await apiClient.post('/api/residents_status', payload)
  return data
}

export async function fetchResidentDefault(
  userId: string | number,
  applicationId?: string | number,
  options?: {
    reimbursement_detail?: boolean
    /** Angular route pagetype: resident uses `/api/resident`, reimbursement uses `/api/default`. */
    pageType?: 'resident-detail' | 'default-detail'
    /** When set, backend loads/updates that reimbursement row (not latest active). */
    reimbursementId?: string | number
  },
): Promise<ResidentDetailResponse> {
  const uid = normalizeNumericPathSegment(userId)
  const appParam =
    applicationId !== undefined && applicationId !== null && String(applicationId).trim() !== ''
      ? normalizeNumericPathSegment(applicationId as string | number)
      : undefined
  const pageType = options?.pageType
  // Angular PR #1170 loadResidentDetail:
  // resident-detail → GET /api/resident/:userId?page=resident
  // default-detail  → GET /api/default/:userId?page=default
  // (no pageType)   → GET /api/default/:userId?page=resident — list bulk / legacy callers
  const page = pageType === 'default-detail' ? 'default' : 'resident'
  const params: Record<string, unknown> = {
    page,
  }
  if (options?.reimbursement_detail) {
    params.reimbursement_detail = true
  }
  if (appParam) {
    params.application_id = appParam
  }
  if (
    options?.reimbursementId !== undefined &&
    options.reimbursementId !== null &&
    String(options.reimbursementId).trim() !== ''
  ) {
    params.reimbursement_id = String(options.reimbursementId)
  }
  const path =`/api/default/${uid}`
  const { data } = await apiClient.get<ResidentDetailResponse>(path, {
    params,
  })
  return normalizeDefaultDetailGetResponse(data, pageType)
}

export async function saveResidentDefault(
  userId: string | number,
  payload: Record<string, unknown> | FormData,
): Promise<ResidentDetailResponse> {
  /** FormData: interceptor strips default JSON Content-Type so the browser sets multipart + boundary (Angular `upload: true`). */
  const uid = normalizeNumericPathSegment(userId)
  const { data } = await apiClient.post<ResidentDetailResponse>(`/api/default/${uid}`, payload)
  return unwrapResidentDetailResponse(data)
}

export async function fetchApplicationInfo(applicantId: string | number): Promise<ResidentApplicationData> {
  const segment =
    typeof applicantId === 'string' && /^\d+$/.test(applicantId.trim())
      ? String(Number(applicantId.trim()))
      : String(applicantId)
  const { data } = await apiClient.get<ResidentApplicationData>(
    `/api/application_info/${segment}`,
    { params: { reimbursement_detail: true } },
  )
  return unwrapApplicationResponse(data)
}

export async function fetchRentalList(): Promise<RentalListResponse> {
  const statusList = await fetchStatusList()
  return buildRentalListFromStatusList(statusList)
}

/**
 * Cancel default reimbursement. Angular `DefaultClaimComponent` posts:
 * `default_claim`, `reason_note`, `email_sendto`, `send_to`, `email_sendtoproperty`.
 * Older flows may post an empty body.
 */
export async function cancelDefaultClaim(
  defaultId: string | number,
  payload: Record<string, unknown> = {},
): Promise<unknown> {
  const { data } = await apiClient.post(`/api/default_cancel_claim/${defaultId}`, payload)
  return data
}

/**
 * Upload settlement statement documents. Mirrors Angular `uploadOldStatement`:
 * `POST /api/default/old_statement/:id` with multipart fields `attachment[0]`, …
 * Settlement statements are reimbursement-scoped on the backend.
 */
export async function uploadOldStatement(
  defaultId: string | number,
  files: File[],
  options?: { page?: 'resident' | 'default'; rental_status?: string },
): Promise<unknown> {
  const formData = new FormData()
  if (options?.page) {
    formData.append('page', options.page)
  }
  if (options?.rental_status) {
    formData.append('rental_status', options.rental_status)
  }
  files.forEach((file, index) => {
    formData.append(`attachment[${index}]`, file)
  })
  const { data } = await apiClient.post(`/api/default/old_statement/${defaultId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

export async function deleteDefaultFile(
  fileId: string | number,
  options?: { page?: 'resident' | 'default' },
): Promise<unknown> {
  const { data } = await apiClient.delete(`/api/default/${fileId}`, {
    params: options?.page ? { page: options.page } : undefined,
  })
  return data
}

export async function reviewResidentStatus(defaultId: string | number, payload: Record<string, unknown>): Promise<unknown> {
  const { data } = await apiClient.post(`/api/resident_review_status/${defaultId}`, payload)
  return data
}

export async function saveClaimReviewLog(reviewLogId: string | number, payload: Record<string, unknown>): Promise<unknown> {
  const { data } = await apiClient.post(`/api/claim_review_log/${reviewLogId}`, payload)
  return data
}

export async function reviewResidentClaim(defaultId: string | number, payload: Record<string, unknown>): Promise<unknown> {
  const { data } = await apiClient.post(`/api/resident_claim_review_status/${defaultId}`, payload)
  return data
}

export async function deletePaymentLog(paymentLogId: string | number): Promise<unknown> {
  const { data } = await apiClient.delete(`/api/payment_log/${paymentLogId}`)
  return data
}

export async function saveDefaultPayment(defaultId: string | number, payload: Record<string, unknown>): Promise<unknown> {
  const { data } = await apiClient.post(`/api/default_payment/${defaultId}`, payload)
  return data
}

export async function defaultPaymentApproved(id: string | number, payload: Record<string, unknown>): Promise<unknown> {
  const { data } = await apiClient.post(`/api/default_payment_approved/${id}`, payload)
  return data
}

export async function fetchNotes(
  defaultId: string | number,
  params?: Record<string, string>,
): Promise<unknown> {
  const { data } = await apiClient.get(`/api/notes/${defaultId}`, {
    params: params && Object.keys(params).length ? params : undefined,
  })
  return data
}

export async function saveNote(defaultId: string | number, payload: Record<string, unknown>): Promise<unknown> {
  const { data } = await apiClient.post(`/api/notes/${defaultId}`, payload)
  return data
}

export async function deleteNoteOrMessage(id: string | number, type: 'notes' | 'message' = 'notes'): Promise<unknown> {
  const { data } = await apiClient.delete(`/api/${type}/${id}`)
  return data
}

/**
 * Mirrors Angular's `excludeAuto`. The API expects `'1'` to exclude and
 * `'2'` to include — `'0'` is not a valid value.
 */
export async function updateExcludeAuto(
  defaultId: string | number,
  exclude: boolean,
): Promise<unknown> {
  const { data } = await apiClient.post(`/api/default_exclude_auto/${defaultId}`, {
    exclude_auto: exclude ? '1' : '2',
  })
  return data
}

/**
 * Mirrors Angular's `claimDenyReopen`. Expects `type: 'claim_denied' | 'claim_reopened'`.
 * `net_amount` and `adjustment_data` are optional.
 */
export async function claimDenyReopen(
  defaultId: string | number,
  payload: ClaimDenyReopenPayload,
): Promise<unknown> {
  const { data } = await apiClient.post(`/api/default_denyreopen_claim/${defaultId}`, payload)
  return data
}

/**
 * Mirrors Angular's `forfeitDefault`. The API does not expect a body.
 */
export async function forfeitDefault(defaultId: string | number): Promise<unknown> {
  const { data } = await apiClient.post(`/api/default_forfeit/${defaultId}`, {})
  return data
}

/**
 * Generate the Default Settlement Statement. Mirrors Angular's
 * `defaultPaymentStatement` flow:
 * `{ default_data, calculation_data }`.
 */
export async function generateSettlementStatement(
  defaultId: string | number,
  payload: Record<string, unknown>,
): Promise<unknown> {
  const { data } = await apiClient.post(`/api/default_payment_statement/${defaultId}`, payload)
  return data
}

/**
 * Email the Default Settlement Statement. Mirrors Angular's
 * `defaultPaymentStatementEmail` flow.
 */
export async function emailSettlementStatement(
  defaultId: string | number,
  payload: Record<string, unknown>,
): Promise<unknown> {
  const { data } = await apiClient.post(`/api/default_payment_statement_email/${defaultId}`, payload)
  return data
}

/**
 * Upload Liberty Internal evidence files. Mirrors Angular `uploadDocuments()`:
 * `POST /api/attachments/default/:defaultId` with `attachment[0]`, `attachment[1]`, …
 */
export async function uploadLibertyDocument(
  defaultId: string | number,
  payload: FormData,
): Promise<unknown> {
  const { data } = await apiClient.post(`/api/attachments/default/${defaultId}`, payload, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

/**
 * Remove a Liberty Internal attachment. Mirrors Angular `deleteFile`:
 * `DELETE /api/attachments/default/:file_id`
 */
export async function deleteLibertyDocument(attachmentId: string | number): Promise<unknown> {
  const { data } = await apiClient.delete(`/api/attachments/default/${attachmentId}`)
  return data
}

/**
 * List Liberty Internal attachments. Mirrors Angular `loadDocuments()`:
 * `GET /api/attachments/default/:defaultId`
 */
export async function fetchLibertyDocuments(defaultId: string | number): Promise<unknown> {
  const { data } = await apiClient.get(`/api/attachments/default/${defaultId}`)
  return data
}

export async function fetchPolicyDeclarationFiles(applicationId: string | number): Promise<unknown> {
  const { data } = await apiClient.get(`/policy-declaration-files/${applicationId}`)
  return data
}

export async function fetchFrequentlyUsedNotes(): Promise<unknown> {
  const { data } = await apiClient.get('/api/frequently-used-notes')
  return data
}

export async function orderGuidance(defaultId: string | number, payload: Record<string, unknown>): Promise<unknown> {
  const { data } = await apiClient.post(`/api/order_guidance/${defaultId}`, payload)
  return data
}

export async function addGuidance(defaultId: string | number, payload: FormData): Promise<unknown> {
  const { data } = await apiClient.post(`/api/add_guidance/${defaultId}`, payload, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data
}

/**
 * Active Liberty users for internal-note @mention tagging (Angular `liberty_loadTaggingUser`).
 */
export async function fetchLibertyTaggingUsers(): Promise<Array<{ name?: string; user_id?: string | number }>> {
  const { data } = await apiClient.get<{ data?: Array<{ name?: string; user_id?: string | number }> }>(
    '/api/user',
    { params: { organization_type: 'LIBERTY', perPage: 100, account_status: 'Active' } },
  )
  return data?.data ?? []
}
